import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Settings2, Check, Play, Loader2, Sparkles } from "lucide-react";
import { getRate, setRate, getVoice, setVoice, SERVER_VOICES } from "@/lib/audioReader";
import { api } from "@/lib/kfmApi";

const SPEEDS = [0.75, 1, 1.25, 1.5];
const PREMIUM_PREVIEW_TEXT = "The Lord is my shepherd; I shall not want. He makes me lie down in green pastures.";

export default function AudioSettings() {
  const [rate, setR] = useState(getRate());
  const [voice, setV] = useState(getVoice());
  const [previewing, setPreviewing] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [premiumVoices, setPremiumVoices] = useState([]);
  const previewAudioRef = useRef(null);

  useEffect(() => () => { previewAudioRef.current?.pause(); }, []);
  useEffect(() => {
    api.get("/tts/premium-voices").then(({ data }) => setPremiumVoices(data.voices || [])).catch(() => {});
  }, []);

  const chooseRate = (r) => { setRate(r); setR(r); };
  const chooseVoice = (key) => { setVoice(key); setV(key); };

  const playPreview = async (key) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      URL.revokeObjectURL(previewAudioRef.current.src);
      previewAudioRef.current = null;
    }
    if (previewing === key) { setPreviewing(null); return; }
    setPreviewLoading(key);
    try {
      const data = premiumVoices.some((v) => v.key === key)
        ? (await api.post("/tts", { text: PREMIUM_PREVIEW_TEXT, voice: key }, { responseType: "blob" })).data
        : (await api.get(`/tts/voices/${key}/preview.mp3`, { responseType: "blob" })).data;
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audio.onended = () => setPreviewing((cur) => (cur === key ? null : cur));
      previewAudioRef.current = audio;
      await audio.play();
      setPreviewing(key);
    } catch (e) { /* preview is best-effort, never surfaced as an error */ }
    setPreviewLoading(null);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="audio-settings-trigger"
          title="Voice & speed"
          aria-label="Voice and speed"
          className="inline-flex items-center justify-center rounded-full w-9 h-9 text-[#00E5FF] hover:text-[#00B8CC] hover:bg-[#10222A] transition-colors"
        >
          <Settings2 className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" data-testid="audio-settings-panel">
        <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2">Speed</div>
        <div className="grid grid-cols-4 gap-1.5 mb-4">
          {SPEEDS.map((s) => (
            <button
              key={s}
              data-testid={`speed-option-${s}`}
              onClick={() => chooseRate(s)}
              aria-pressed={rate === s}
              className={`rounded-lg py-1.5 text-sm font-medium transition-colors ${
                rate === s ? "bg-[#00E5FF] text-[#07090E]" : "bg-[#141B29] text-slate-400 hover:bg-[#1E273D]"
              }`}
            >
              {s}×
            </button>
          ))}
        </div>

        <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2">Voice</div>
        <div className="space-y-0.5" data-testid="voice-list">
          {SERVER_VOICES.map((v) => (
            <VoiceRow
              key={v.key}
              label={v.label}
              active={voice === v.key}
              previewing={previewing === v.key}
              previewLoading={previewLoading === v.key}
              onClick={() => chooseVoice(v.key)}
              onPreview={() => playPreview(v.key)}
              testid={`voice-option-${v.key}`}
            />
          ))}
        </div>
        {premiumVoices.length > 0 && (
          <>
            <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2 mt-4 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={2} /> Premium (Pro)
            </div>
            <div className="space-y-0.5" data-testid="premium-voice-list">
              {premiumVoices.map((v) => (
                <VoiceRow
                  key={v.key}
                  label={v.label}
                  active={voice === v.key}
                  previewing={previewing === v.key}
                  previewLoading={previewLoading === v.key}
                  onClick={() => chooseVoice(v.key)}
                  onPreview={() => playPreview(v.key)}
                  testid={`voice-option-${v.key}`}
                />
              ))}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function VoiceRow({ label, active, previewing, previewLoading, onClick, onPreview, testid }) {
  return (
    <div
      className={`w-full flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-left text-sm transition-colors ${
        active ? "bg-[#10222A] text-[#00B8CC]" : "text-slate-400 hover:bg-[#141B29]"
      }`}
    >
      <button
        data-testid={`${testid}-preview`}
        onClick={(e) => { e.stopPropagation(); onPreview(); }}
        title={`Hear a sample of ${label}`}
        aria-label={`Preview ${label}`}
        className="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-6 sm:h-6 rounded-full text-[#00E5FF] hover:text-[#00B8CC] hover:bg-[#0E131F]/70 transition-colors"
      >
        {previewLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
        ) : (
          <Play className={`w-3 h-3 ${previewing ? "text-[#00E5FF]" : ""}`} strokeWidth={2} fill={previewing ? "currentColor" : "none"} />
        )}
      </button>
      <button data-testid={testid} onClick={onClick} className="flex-1 flex items-center justify-between gap-2 py-1.5 pr-1">
        <span className="truncate">{label}</span>
        {active && <Check className="w-4 h-4 text-[#00E5FF] shrink-0" strokeWidth={2} />}
      </button>
    </div>
  );
}
